import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container, Divider, Spacer, Typography, useSnackbar } from '@emburse/embark-core';
import { RadioGroup } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import SSOFields from './SSOFields';
import CertificateField from './CertificateField';
import WelcomeEmail from './WelcomeEmail';
import ConfirmationModal from './ConfirmationModal';
import { logger } from '../../util/logger';
import { disableSSO, getSSOModelAndWelcomeEmail, generateWelcomeEmail, updateSSOConfiguration } from '../../api/sso';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
function SingleSignOn() {
    useTranslation();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const ssoFieldsRef = useRef(null);
    const certificateFieldRef = useRef(null);
    const originalRequireSSORef = useRef(null);
    const didMount = useRef(false);
    const { enqueueSnackbar } = useSnackbar();
    const [useSSO, setUseSSO] = useState('0');
    const [model, setModel] = useState();
    const [certificate, setCertificate] = useState('');
    const [defaultWelcomeEmail, setDefaultWelcomeEmail] = useState();
    const confirmModalDefaults = {
        open: false,
        onConfirm: () => { },
        onClose: () => { },
        bodyText: ''
    };
    const [confirmModal, setConfirmModal] = useState(confirmModalDefaults);
    const [certificateValidationText, setCertificateValidationText] = useState('');
    const [isCodeNotValid, setIsCodeNotValid] = useState(false);
    const isSSO = (dataModel) => {
        // if any of the SSO company prefs have values, show SSO as selected
        const vals = Object.values(dataModel);
        const hasVal = vals.filter((val) => {
            if (Array.isArray(val) && !val.length) {
                return val.length;
            }
            return val;
        });
        return hasVal.length > 0;
    };
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    useEffect(() => {
        applyLoadingState(30000);
        getSSOModelAndWelcomeEmail()
            .then((data) => {
            const serverData = JSON.parse(JSON.stringify(data));
            setUseSSO(isSSO(serverData.Model) ? '1' : '0');
            setModel(serverData.Model);
            originalRequireSSORef.current = serverData.Model.RequireSSO;
            setDefaultWelcomeEmail(serverData.DefaultWelcomeEmail);
        })
            .catch((err) => {
            logger.log(err);
            snackbarHandler(err.Message, 'error');
        })
            .finally(() => {
            didMount.current = true;
            removeLoadingState();
        });
    }, [applyLoadingState, removeLoadingState, snackbarHandler]);
    const isUseSSO = () => useSSO === '1';
    const updateModel = (data) => {
        setModel((prevState) => ({ ...prevState, ...data }));
    };
    const restoreDefault = () => {
        setConfirmModal({
            open: true,
            onClose: () => setConfirmModal(confirmModalDefaults),
            bodyText: $t('Are you sure you want to restore the default SSO Welcome Email? Doing so will remove any edits you have made.'),
            onConfirm: () => {
                const email = document.getElementById('EmailBody');
                email.innerHTML = null;
                updateModel({ CustomWelcomeEmail: defaultWelcomeEmail });
            }
        });
    };
    const deleteThumbprint = (thumbprint) => {
        const thumbprintArray = JSON.parse(JSON.stringify(model.X509Thumbprint));
        const updatedThumbprints = thumbprintArray.filter((item) => item !== thumbprint);
        updateModel({ X509Thumbprint: updatedThumbprints });
    };
    const sendTestEmail = () => {
        applyLoadingState(30000);
        generateWelcomeEmail({ WelcomeEmail: model.CustomWelcomeEmail || defaultWelcomeEmail })
            .then((resp) => {
            if (resp.Status === 'success') {
                snackbarHandler(resp.Message, 'success');
            }
        })
            .catch((err) => {
            logger.log(err);
            snackbarHandler(err.Message, 'error');
        })
            .finally(() => removeLoadingState());
    };
    const responseHandler = (resp) => {
        if (resp.Status === 'success') {
            const data = JSON.parse(resp.Data);
            setModel(data);
            snackbarHandler(resp.Message, 'success');
            setCertificate('');
            originalRequireSSORef.current = data.RequireSSO;
            if (certificateFieldRef.current) {
                certificateFieldRef.current.showCertificateField(false);
            }
        }
        if (resp.Status === 'reject') {
            if (resp.Message.includes('Certificate')) {
                setCertificateValidationText(resp.Message);
            }
            else if (resp.Message.includes('code')) {
                if (ssoFieldsRef.current) {
                    ssoFieldsRef.current.setValidationText(resp.Message);
                }
            }
            snackbarHandler(resp.Message, 'error');
        }
    };
    const doStandardSave = () => {
        applyLoadingState(30000);
        updateSSOConfiguration({
            Model: model,
            X509CertificateString: certificate || ''
        })
            .then(responseHandler)
            .catch((err) => {
            logger.log(err);
            snackbarHandler(err.Message, 'error');
        })
            .finally(() => removeLoadingState());
    };
    const doIsSSOSave = () => {
        setConfirmModal({
            open: true,
            onClose: () => setConfirmModal(confirmModalDefaults),
            bodyText: $t('Changing authentication type to standard will permanently delete all SSO settings. Are you sure you want to continue?'),
            onConfirm: () => {
                applyLoadingState(30000);
                disableSSO({ Model: {} })
                    .then(responseHandler)
                    .catch((err) => {
                    logger.log(err);
                    snackbarHandler(err.Message, 'error');
                })
                    .finally(() => removeLoadingState());
            }
        });
    };
    const doNonStandardLoginSave = () => {
        setConfirmModal({
            open: true,
            onClose: () => setConfirmModal(confirmModalDefaults),
            bodyText: $t(`Disabling standard login will immediately disallow all users in your company from logging in 
            with their Certify Username and Password. Are you sure you want to continue?`),
            onConfirm: () => {
                doStandardSave();
            }
        });
    };
    const saveHandler = () => {
        // if changing configuration from SSO to non-SSO
        if (!isUseSSO() && isSSO(model)) {
            return doIsSSOSave();
        }
        // if disabling standard login
        if (model.RequireSSO === '1' && originalRequireSSORef.current !== '1') {
            return doNonStandardLoginSave();
        }
        // regular update
        return doStandardSave();
    };
    const authTypeControls = [
        {
            formControl: {
                value: '0',
                label: (_jsxs(Typography, { children: [_jsx("strong", { children: $t('Standard') }), $t(' - My users log in with an Emburse username and password (most common)')] })),
                onChange: () => setUseSSO('0')
            },
            radio: {
                color: 'primary'
            }
        },
        {
            formControl: {
                value: '1',
                label: (_jsxs(Typography, { children: [_jsx("strong", { children: $t('Single Sign On') }), $t(' - My users log in through an Identity Provider (IdP)')] })),
                onChange: () => setUseSSO('1')
            },
            radio: {
                color: 'primary'
            }
        }
    ];
    return (_jsx(SingleColumnLayout, { children: didMount.current && (_jsxs(_Fragment, { children: [_jsx(ConfirmationModal, { open: confirmModal.open, onClose: confirmModal.onClose, onConfirm: confirmModal.onConfirm, bodyText: confirmModal.bodyText }), _jsx(Typography, { variant: "h4", children: $t('Account Access') }), _jsx(Typography, { children: $t('Configure how your organization authenticates in to the application') }), _jsx(Spacer, { size: 48 }), _jsx(Typography, { children: _jsx("strong", { children: $t('Authentication Type') }) }), _jsx(RadioGroup, { name: "AuthType", defaultValue: useSSO, value: useSSO, controls: authTypeControls }), isUseSSO() && (_jsxs(_Fragment, { children: [_jsxs(Container, { disableGutters: true, maxWidth: "sm", style: { marginLeft: '0' }, children: [_jsx(Spacer, { size: 15 }), _jsx(SSOFields, { model: model, updateModel: updateModel, setIsNotValid: setIsCodeNotValid, ref: ssoFieldsRef }), _jsx(CertificateField, { X509Thumbprint: model.X509Thumbprint, certificate: certificate, updateCertificate: (value) => {
                                        setCertificate(value);
                                        setCertificateValidationText('');
                                    }, deleteThumbprint: deleteThumbprint, certificateValidationText: certificateValidationText, ref: certificateFieldRef })] }), _jsx(WelcomeEmail, { defaultWelcomeEmail: defaultWelcomeEmail, customWelcomeEmail: model.CustomWelcomeEmail, updateEmail: (data) => updateModel({ CustomWelcomeEmail: data }), sendTestEmail: sendTestEmail, restoreDefault: restoreDefault })] })), _jsx(Spacer, { size: 48 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 10 }), _jsx(Button, { color: "primary", onClick: saveHandler, disabled: certificateValidationText.length > 0 || isCodeNotValid, children: $t('Save') })] })) }));
}
export default SingleSignOn;
